<template>
  <transition name="fade">
    <div v-if="visible" class="loading-spinner-overlay" role="status" :aria-label="t('player.status.loading')">
      <div class="loading-spinner">
        <div class="spinner-ring"></div>
        <div class="spinner-ring"></div>
        <div class="spinner-ring"></div>
        <span class="spinner-text">{{ t('player.status.loading') }}</span>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { useVideoI18n } from '../../i18n'

defineProps<{ visible: boolean }>()

const { t } = useVideoI18n()
</script>

<style scoped lang="scss">
.loading-spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
}

.spinner-text {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  color: var(--vp-control-text-color, #ffffff);
  font-size: 0.875rem;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-radius: 50%;
  box-sizing: border-box;
}

.spinner-ring:nth-child(1) {
  border-width: 4px;
  border-top-color: var(--vp-primary-color, #007bff);
  border-right-color: var(--vp-primary-color, #007bff);
  animation: spin 1.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.spinner-ring:nth-child(2) {
  border-width: 4px;
  border-top-color: var(--vp-spinner-ring, rgba(255, 255, 255, 0.9));
  border-left-color: var(--vp-spinner-ring-faint, rgba(255, 255, 255, 0.6));
  width: 70%;
  height: 70%;
  top: 15%;
  left: 15%;
  animation: spin-reverse 2s cubic-bezier(0.445, 0.05, 0.55, 0.95) infinite;
}

.spinner-ring:nth-child(3) {
  border-width: 3px;
  border-top-color: var(--vp-spinner-ring-faint, rgba(255, 255, 255, 0.7));
  border-bottom-color: var(--vp-spinner-ring-subtle, rgba(255, 255, 255, 0.4));
  width: 45%;
  height: 45%;
  top: 27.5%;
  left: 27.5%;
  animation: spin 2.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes spin-reverse {
  0% { transform: rotate(360deg); }
  100% { transform: rotate(0deg); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .loading-spinner {
    width: 64px;
    height: 64px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner-ring {
    animation: none;
    border-color: var(--vp-spinner-ring-subtle, rgba(255, 255, 255, 0.3));
  }

  .spinner-ring:nth-child(1) {
    border-top-color: var(--vp-primary-color, #007bff);
    border-right-color: var(--vp-primary-color, #007bff);
  }

  .spinner-ring:nth-child(2) {
    border-top-color: var(--vp-spinner-ring, rgba(255, 255, 255, 0.9));
    border-left-color: var(--vp-spinner-ring-faint, rgba(255, 255, 255, 0.6));
  }

  .spinner-ring:nth-child(3) {
    border-top-color: var(--vp-spinner-ring-faint, rgba(255, 255, 255, 0.7));
    border-bottom-color: var(--vp-spinner-ring-subtle, rgba(255, 255, 255, 0.4));
  }

  .spinner-text {
    position: static;
    width: auto;
    height: auto;
    padding: 0;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
    display: block;
    text-align: center;
    padding-top: 8px;
  }

  .loading-spinner {
    width: auto;
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .spinner-ring {
    position: relative;
    top: auto;
    left: auto;
  }

  .spinner-ring:nth-child(1) {
    width: 48px;
    height: 48px;
  }

  .spinner-ring:nth-child(2),
  .spinner-ring:nth-child(3) {
    display: none;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition-duration: 1ms;
  }
}
</style>
